/**
 * PWA Utility Functions
 * Detect PWA mode, device type, and handle installation
 */

// Check if running inside a Capacitor native app (iOS/Android)
export function isNativeApp(): boolean {
  return !!(window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.();
}

// Check if running as installed PWA
export function isPWA(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true ||
    document.referrer.includes('android-app://')
  );
}

// Check if iOS device
export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as unknown as { MSStream?: unknown }).MSStream;
}

// Check if Android device
export function isAndroid(): boolean {
  return /Android/.test(navigator.userAgent);
}

// Check if mobile device (iOS or Android)
export function isMobile(): boolean {
  return isIOS() || isAndroid();
}

// Check if Safari browser on iOS
export function isIOSSafari(): boolean {
  return isIOS() && /Safari/.test(navigator.userAgent) && !/CriOS/.test(navigator.userAgent);
}

// Get iOS version
export function getIOSVersion(): number | null {
  if (!isIOS()) return null;

  const match = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
  if (!match) return null;

  const major = parseInt(match[1], 10);
  const minor = parseInt(match[2], 10);

  // Return as single number: 16.4.0 -> 16.4
  return parseFloat(`${major}.${minor}`);
}

// Check if iOS version supports web push (16.4+)
export function isIOSPushSupported(): boolean {
  const version = getIOSVersion();
  return version !== null && version >= 16.4;
}

// Check if browser supports push notifications
export function isPushNotificationSupported(): boolean {
  return (
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

// Check if iOS and push is supported
export function canRequestIOSPush(): boolean {
  return isIOS() && isIOSPushSupported() && isPushNotificationSupported();
}

// Request persistent storage (helps prevent data clearing on iOS)
export async function requestPersistentStorage(): Promise<boolean> {
  if (navigator.storage && navigator.storage.persist) {
    try {
      const granted = await navigator.storage.persist();
      console.log('[PWA] Persistent storage:', granted ? 'granted' : 'denied');
      return granted;
    } catch (error) {
      console.log('[PWA] Persistent storage request failed:', error);
      return false;
    }
  }
  return false;
}

// Check if persistent storage is already granted
export async function isPersistentStorageGranted(): Promise<boolean> {
  if (navigator.storage && navigator.storage.persisted) {
    try {
      return await navigator.storage.persisted();
    } catch {
      return false;
    }
  }
  return false;
}

// Get storage quota info
export async function getStorageInfo(): Promise<{ used: number; quota: number } | null> {
  if (navigator.storage && navigator.storage.estimate) {
    try {
      const estimate = await navigator.storage.estimate();
      return {
        used: estimate.usage || 0,
        quota: estimate.quota || 0,
      };
    } catch {
      return null;
    }
  }
  return null;
}

// Service worker registration config
interface ServiceWorkerConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
}

// Register service worker
// Skip on Capacitor native apps — service worker caching is unnecessary since
// the native app loads from the production server (remote URL mode) and all
// Capacitor plugins use the native bridge, not fetch interception.
export async function registerServiceWorker(config?: ServiceWorkerConfig): Promise<ServiceWorkerRegistration | null> {
  if (isNativeApp()) {
    console.log('[PWA] Skipping service worker registration on Capacitor native app');
    return null;
  }
  // Skip SW registration in local dev mode to avoid intercepting API calls
  if (process.env.REACT_APP_ENV === 'development') {
    console.log('[PWA] Skipping service worker in local dev');
    return null;
  }
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('[PWA] Service worker registered:', registration.scope);

      // Best-effort eviction of the legacy /sw.js orphan SW (sunset 2026-05-10).
      // Guarded against missing getRegistrations so a TypeError can't abort the
      // surrounding registration setup (controllerchange listener, onupdatefound).
      if (typeof navigator.serviceWorker.getRegistrations === 'function') {
        navigator.serviceWorker.getRegistrations().then((regs) => {
          regs.forEach((reg) => {
            if (reg.active?.scriptURL?.endsWith('/sw.js')) {
              reg.unregister();
            }
          });
        }).catch(() => { /* eviction is best-effort */ });
      }

      // Reload exactly once when the new SW takes control via skipWaiting + clients.claim.
      // controllerchange fires after clients.claim() completes — page is fully loaded by
      // then, so reloading is safe on both first install and updates. We do NOT gate on
      // hadController because that would require a manual quit-and-reopen on first install.
      //
      // We do NOT reload in onstatechange:'installed' — our SW calls skipWaiting() on
      // install, which activates before onstatechange fires on the client. Reloading at
      // 'installed' causes iOS WKWebView to lose its fetch context mid-load, caching
      // offline.html for subsequent navigations.
      // (Incident 2026-04-17: ~2min downtime; users stuck on offline page post-recovery.)
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) {
          refreshing = true;
          console.log('[PWA] New SW active (controllerchange), reloading...');
          config?.onUpdate?.(registration);
          window.location.reload();
        }
      });

      // Handle updates — track first install success; reload is handled by controllerchange
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (!installingWorker) return;

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed' && !navigator.serviceWorker.controller) {
            // First install only — content cached for offline use
            console.log('[PWA] Content cached for offline use.');
            config?.onSuccess?.(registration);
          }
        };
      };

      // Check for updates every 5 minutes (catches deploys quickly)
      setInterval(() => {
        registration.update();
      }, 5 * 60 * 1000);

      // Also check on visibility change (user returns to tab/app)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          registration.update();
        }
      });

      return registration;
    } catch (error) {
      console.log('[PWA] Service worker registration failed:', error);
      return null;
    }
  }
  return null;
}

// Check if app can be installed (Android)
export function canInstallPWA(): boolean {
  return 'BeforeInstallPromptEvent' in window || isIOSSafari();
}

// Local storage keys for PWA prompts
const PWA_PROMPT_DISMISSED_KEY = 'pwa-install-prompt-dismissed';
const PWA_PROMPT_DISMISSED_TIME_KEY = 'pwa-install-prompt-dismissed-time';
const PWA_PROMPT_SHOW_COUNT_KEY = 'pwa-install-prompt-show-count';

// Check if user has dismissed the install prompt
export function hasUserDismissedInstallPrompt(): boolean {
  const dismissed = localStorage.getItem(PWA_PROMPT_DISMISSED_KEY);
  if (dismissed !== 'true') return false;

  // Check if it's been more than 7 days since dismissal (allow re-prompting)
  const dismissedTime = localStorage.getItem(PWA_PROMPT_DISMISSED_TIME_KEY);
  if (dismissedTime) {
    const daysSinceDismissal = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
    if (daysSinceDismissal > 7) {
      // Reset dismissal after 7 days
      localStorage.removeItem(PWA_PROMPT_DISMISSED_KEY);
      localStorage.removeItem(PWA_PROMPT_DISMISSED_TIME_KEY);
      return false;
    }
  }

  return true;
}

// Mark install prompt as dismissed
export function dismissInstallPrompt(): void {
  localStorage.setItem(PWA_PROMPT_DISMISSED_KEY, 'true');
  localStorage.setItem(PWA_PROMPT_DISMISSED_TIME_KEY, Date.now().toString());
}

// Get/increment prompt show count
export function getPromptShowCount(): number {
  return parseInt(localStorage.getItem(PWA_PROMPT_SHOW_COUNT_KEY) || '0');
}

export function incrementPromptShowCount(): void {
  const count = getPromptShowCount() + 1;
  localStorage.setItem(PWA_PROMPT_SHOW_COUNT_KEY, count.toString());
}

// Should show install prompt?
export function shouldShowInstallPrompt(): boolean {
  // Don't show if already in PWA mode
  if (isPWA()) return false;

  // Don't show if not mobile
  if (!isMobile()) return false;

  // Don't show if user dismissed recently
  if (hasUserDismissedInstallPrompt()) return false;

  // Don't show too many times (max 3 times)
  if (getPromptShowCount() >= 3) return false;

  return true;
}
